import React, { useEffect, useState } from "react";
import "../styles/home.css";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useTranslation } from "react-i18next";
import Constants from "../constants";
import InfoIcon from "@mui/icons-material/Info";
import { useAuth } from "../context/AuthContext";
import SkeletonWrapper from "../components/SkeletonWrapper";
import { useLoading } from "../hooks/useLoading";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getTimeOnly } from "../utilities/convertUtcToTimeZone";
import timezone from "dayjs/plugin/timezone";
import PromotionalBanner from "../components/promotionalBanner";

dayjs.extend(utc);
dayjs.extend(timezone);
const userTimezone = dayjs.tz.guess();
const BLOB_STORAGE_URL = process.env.REACT_APP_BLOB_STORAGE_URL;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const initialCategories = [
  {
    value: Constants.ENTITY.SHC,
    entity: Constants.ENTITY.SHC,
    label: Constants.TAB_NAMES.SHC,
    imageUrlEN: `${BLOB_STORAGE_URL}/vmco-homeimages/SandwichesEN.png`,
    imageUrlAR: `${BLOB_STORAGE_URL}/vmco-homeimages/SandwichesAr.png`,
  },
  {
    value: Constants.CATEGORY.VMCO_CONSUMABLES,
    entity: Constants.ENTITY.VMCO,
    label: Constants.TAB_NAMES.VMCO_CONSUMABLES,
    imageUrlEN: `${BLOB_STORAGE_URL}/vmco-homeimages/FoodingredientsEn.png`,
    imageUrlAR: `${BLOB_STORAGE_URL}/vmco-homeimages/FoodingredientsAr.png`,
  },
  {
    value: Constants.ENTITY.GMTC,
    entity: Constants.ENTITY.GMTC,
    label: Constants.TAB_NAMES.GMTC,
    imageUrlEN: `${BLOB_STORAGE_URL}/vmco-homeimages/FreshVegetablesEN.png`,
    imageUrlAR: `${BLOB_STORAGE_URL}/vmco-homeimages/FreshVegetablesAr.png`,
  },
  {
    value: Constants.ENTITY.NAQI,
    entity: Constants.ENTITY.NAQI,
    label: Constants.TAB_NAMES.NAQI,
    imageUrlEN: `${BLOB_STORAGE_URL}/vmco-homeimages/HygieneChemicalsEN.png`,
    imageUrlAR: `${BLOB_STORAGE_URL}/vmco-homeimages/HygieneChemicalsAr.png`,
  },
  {
    value: Constants.CATEGORY.VMCO_MACHINES,
    entity: Constants.ENTITY.VMCO,
    label: Constants.TAB_NAMES.VMCO_MACHINES,
    imageUrlEN: `${BLOB_STORAGE_URL}/vmco-homeimages/MachinesEN.png`,
    imageUrlAR: `${BLOB_STORAGE_URL}/vmco-homeimages/MachinesAr.png`,
  },
  {
    value: "SPECIAL_PRODUCTS",
    entity: "",
    label: "Special Products",
    imageUrlEN: `${BLOB_STORAGE_URL}/vmco-homeimages/SpeacialProductsEn.png`,
    imageUrlAR: `${BLOB_STORAGE_URL}/vmco-homeimages/SpeacialProductsAr.png`,
  },
];

const getEntityFromCategory = (categoryName) => {
  if (!categoryName) return null;
  const category = categoryName.toLowerCase();
  if (category.includes(Constants.ENTITY.VMCO.toLowerCase()) || category.includes("vending machine company")) return Constants.ENTITY.VMCO;
  if (category.includes(Constants.ENTITY.SHC.toLowerCase()) || category.includes("saudi hospitality company")) return Constants.ENTITY.SHC;
  if (category.includes(Constants.ENTITY.GMTC.toLowerCase()) || category.includes("green mast factory ltd")) return Constants.ENTITY.GMTC;
  if (category.includes(Constants.ENTITY.NAQI.toLowerCase()) || category.includes("naqi company")) return Constants.ENTITY.NAQI;
  if (category.includes(Constants.ENTITY.DAR.toLowerCase()) || category.includes("dar company")) return Constants.ENTITY.DAR;
  return null;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token,user } = useAuth();
  const isRTL = i18n.language === "ar";
  const [coolingPeriodData, setCoolingPeriodData] = useState([]);
  const [disabledEntities, setDisabledEntities] = useState([]);
  const { loading, startLoading, stopLoading } = useLoading();
  console.log("Disabled Entities:", user);
  // State for SHC modal
  const [showSHCModal, setShowSHCModal] = useState(false);
  const [selectedSHCOption, setSelectedSHCOption] = useState(null);
  const [pendingEntity, setPendingEntity] = useState(null);

  useEffect(() => {
    const fetchCoolingPeriod = async () => {
      if (!token) return;
      try {
        startLoading();
        const response = await fetch(`${API_BASE_URL}/cooling-period/now`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.status === "Ok" && Array.isArray(result.data)) {
            setCoolingPeriodData(result.data);
            const entities = [...new Set(result.data.map((item) => item.entity))];
            setDisabledEntities(entities);
          }
        }
      } catch (error) {
        console.error("Error fetching cooling period:", error);
      } finally {
        stopLoading();
      }
    };
    fetchCoolingPeriod();
  }, [token, startLoading, stopLoading]);

// Replace the modal section with this alternative using SweetAlert2
const handleNavigation = async (entity) => {
  // Check if entity is SHC
if (entity === Constants.ENTITY.SHC) {
  const showSchool =
    !!user?.isShcSchool && user.userType?.toLowerCase() === "customer";

const result = await Swal.fire({
  title: t("Select Product Category"),
  html: `
    <div class="category-selection-container" dir="${isRTL ? 'rtl' : 'ltr'}">
      
      <!-- Fresh -->
      <button id="btn-fresh" class="category-btn category-btn--fresh">
        <div class="category-btn__content">
          <div class="category-btn__icon-wrapper">
            <svg class="category-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></svg>
          </div>
          <div class="category-btn__text">
            <span class="category-btn__title">${t("Fresh Products")}</span>
            <span class="category-btn__desc">${t("Order before 12:00 PM for next-day")}</span>
          </div>
        </div>
        <div class="category-btn__arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </div>
      </button>
      
      <!-- Frozen -->
      <button id="btn-frozen" class="category-btn category-btn--frozen">
        <div class="category-btn__content">
          <div class="category-btn__icon-wrapper">
            <svg class="category-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="12" y2="12"/><line x1="12" x2="12" y1="2" y2="22"/><path d="m20 16-4-4 4-4"/><path d="m4 8 4 4-4 4"/><path d="m16 4-4 4-4-4"/><path d="m8 20 4-4 4 4"/></svg>
          </div>
          <div class="category-btn__text">
            <span class="category-btn__title">${t("Frozen Products")}</span>
            <span class="category-btn__desc">${t("Long shelf life · stored at −18°C")}</span>
          </div>
        </div>
        <div class="category-btn__arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </div>
      </button>

      ${showSchool ? `
      <!-- School -->
      <button id="btn-school" class="category-btn category-btn--school">
        <div class="category-btn__content">
          <div class="category-btn__icon-wrapper">
            <svg class="category-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
          </div>
          <div class="category-btn__text">
            <span class="category-btn__title">${t("School Products")}</span>
            <span class="category-btn__desc">${t("Canteen range for school customers")}</span>
          </div>
        </div>
        <div class="category-btn__arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </div>
      </button>
      ` : ''}

    </div>
  `,
  showConfirmButton: false, 
  showCancelButton: false,
  showCloseButton: true,
  reverseButtons: isRTL,
  customClass: {
    popup: 'custom-category-popup', 
    title: 'custom-category-title'
  },
  didOpen: () => {
    // 💡 The Fix: Attach the event listeners directly to the buttons
    // We use the 'swal2-html-container' to scope the query so it doesn't conflict with other DOM elements
    const container = document.querySelector('.swal2-html-container');

    container.addEventListener('click', (e) => {
      // Check if the clicked element or its parent is one of our specific buttons
      const btn = e.target.closest('.category-btn');
      
      if (btn) {
        const btnId = btn.id;
        let selectedValue = null;

        if (btnId === 'btn-fresh') selectedValue = 'fresh';
        if (btnId === 'btn-frozen') selectedValue = 'frozen';
        if (btnId === 'btn-school') selectedValue = 'school';

        if (selectedValue) {
          // Resolve the SweetAlert promise with our custom data
          Swal.close({ isConfirmed: true, value: selectedValue });
        }
      }
    });
  }
});

// --- How to check the result ---
if (result.isConfirmed) {
  console.log("User selected:", result.value); // Will output 'fresh', 'frozen', or 'school'
}

  if (result.isConfirmed && result.value?.toLowerCase() === 'fresh') {
    // User selected Fresh Product
    navigate(`/catalog/${Constants.ENTITY.SHC}/fresh`);
  } else if (showSchool && result.isConfirmed && result.value?.toLowerCase() === 'school') {
    // User selected School Product (guarded so it can't fire for non-school users)
    navigate(`/catalog/${Constants.ENTITY.SHC}/school`);
  } else if (result.isConfirmed && result.value?.toLowerCase() === 'frozen') {
    // User selected Frozen Product (cancel button)
    navigate(`/catalog/${Constants.ENTITY.SHC}/frozen`);
  }
  // If dismissed by clicking outside or close button, do nothing
  return;
}
  // For other entities, navigate directly
  navigate(`/catalog/${entity}`);
};

  const handleCloseModal = () => {
    setShowSHCModal(false);
    setPendingEntity(null);
    setSelectedSHCOption(null);
  };

  return (
    <Sidebar title={t("Home")} homePage={"home-page"}>
      <div className="home-page-container">
        <PromotionalBanner />
        <div className="home-hero">
          <div className="hero-content">
            <h1 className="catalog_header">
              {t("Order supplies quickly from your approved catalog")}
            </h1>
            <p className="catalog-subHeader">
              {t("Sandwiches, bakery, ingredients, fresh produce & more.")}
            </p>
          </div>
        </div>

        <div className="categories-section">
          <SkeletonWrapper loading={loading} type="home_card" count={7}>
            <div className="categories-grid">
              {initialCategories.map((cat, idx) => {
                const categoryEntity = getEntityFromCategory(cat?.entity?.toLowerCase());

                return (
                  <div
                    key={idx}
                    className={`category-card`}
                    onClick={() => handleNavigation(cat.value)}
                  >
                    <img
                      src={isRTL ? cat.imageUrlAR : cat.imageUrlEN}
                      alt={cat.label}
                      className="category-img"
                    />
                  </div>
                );
              })}
            </div>
          </SkeletonWrapper>
        </div>

        <div className="featured-section">
          <div className="delivery-note">
            <InfoIcon size={16} />
            {t("Chilled & fresh items ordered before 12:00 PM qualify for next-day dispatch")}
          </div>
        </div>
      </div>

    </Sidebar>
  );
};

export default HomePage;